import { LiveWeekEntryFixtureStats } from "../models/api";
import { PlayerPick } from "../models";

/** Short labels for a player's scoring events, e.g. "2G", "A", "CS", "3b". */
const plural = (n: number, one: string, many: string) => (n === 1 ? one : `${n} ${many}`);

/** Readable labels for a player's scoring events, e.g. "2 Goals", "Assist", "Clean sheet", "3 Bonus". */
export const statChips = (stats: LiveWeekEntryFixtureStats[]): string[] => {
	const chips: string[] = [];
	const value = (id: string) => stats.filter((s) => s.identifier === id).reduce((sum, s) => sum + s.value, 0);
	const points = (id: string) => stats.filter((s) => s.identifier === id).reduce((sum, s) => sum + s.points, 0);

	const goals = value("goals_scored");
	if (goals > 0) chips.push(plural(goals, "Goal", "Goals"));
	const assists = value("assists");
	if (assists > 0) chips.push(plural(assists, "Assist", "Assists"));
	if (value("clean_sheets") > 0 && points("clean_sheets") > 0) chips.push("Clean sheet");
	const saves = value("saves");
	if (points("saves") > 0) chips.push(`${saves} Saves`);
	if (value("penalties_saved") > 0) chips.push("Pen saved");
	if (value("defensive_contribution") > 0 && points("defensive_contribution") > 0) chips.push("Def. contribution");
	const bonus = value("bonus");
	if (bonus > 0) chips.push(`${bonus} Bonus`);
	if (points("goals_conceded") < 0) chips.push(`${value("goals_conceded")} Conceded`);
	if (value("yellow_cards") > 0) chips.push("Yellow card");
	if (value("red_cards") > 0) chips.push("Red card");
	if (value("own_goals") > 0) chips.push("Own goal");
	if (value("penalties_missed") > 0) chips.push("Pen missed");

	return chips;
};

/** All of a player's stat lines across the gameweek's fixtures. */
export const allStats = (player: PlayerPick): LiveWeekEntryFixtureStats[] => (player.data ?? []).flatMap((d) => d.stats);

/** Minutes the player has been on the pitch this gameweek. */
export const minutesPlayed = (player: PlayerPick): number => allStats(player).filter((s) => s.identifier === "minutes").reduce((sum, s) => sum + s.value, 0);

/** Minutes the player played in one fixture. */
export const minutesInFixture = (player: PlayerPick, fixtureId: number): number =>
	((player.data ?? []).find((d) => d.fixture === fixtureId)?.stats ?? []).filter((s) => s.identifier === "minutes").reduce((sum, s) => sum + s.value, 0);

/** Human label for a stat identifier, e.g. "goals_scored" -> "goals scored". */
export const statLabel = (identifier: string) => identifier.replaceAll("_", " ");
